import { useState, useEffect } from 'react';
import {
  getVoiceTemplates,
  createVoiceTemplate,
  updateVoiceTemplate,
  deleteVoiceTemplate,
} from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import './VoicemailTemplatesTab.css';

// Template categories — controls the filter pills at the top of the tab.
// 'keys: null' means show all templates.
const CATEGORIES = [
  {
    id: 'all',
    label: 'All',
    keys: null,
  },
  {
    id: 'ivr',
    label: 'IVR Outbound',
    description: 'Voicemails left automatically when AMD detects a machine during an outbound IVR call',
    keys: ['ivrResupplyVoicemail'],
  },
  {
    id: 'queue',
    label: 'Queue Greetings',
    description: 'Greetings played when a caller enters an inbound queue',
    keys: ['queueGreetingResupply', 'queueGreetingCompliance'],
  },
  {
    id: 'voicemail',
    label: 'Voicemail',
    description: 'Outgoing voicemail messages and leave-a-message prompts',
    keys: ['outgoingVoicemail', 'leaveAMessage', 'voicemailInstructions'],
  },
  {
    id: 'other',
    label: 'Other',
    description: 'After-hours messages and miscellaneous prompts',
    keys: ['afterHours'],
  },
];

// All known template keys — used to populate the key dropdown in the form.
const ALL_TEMPLATE_KEYS = [
  'ivrResupplyVoicemail',
  'outgoingVoicemail',
  'queueGreetingResupply',
  'queueGreetingCompliance',
  'leaveAMessage',
  'voicemailInstructions',
  'afterHours',
];

const PATIENT_TYPES = [
  { value: '', label: 'All Types' },
  { value: '1', label: 'Resupply' },
  { value: '2', label: 'Compliance' },
  { value: '3', label: 'Remote Setup' },
  { value: '4', label: 'Onboarding' },
];

const DEFAULT_FORM = {
  templateKey: 'ivrResupplyVoicemail',
  companyId: '',
  patientTypeId: '',
  messageEnglish: '',
  messageSpanish: '',
  isActive: true,
  displayOrder: 99,
};

export default function VoiceTemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchTemplates(); }, []);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(''), 3000);
    return () => clearTimeout(t);
  }, [successMessage]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      setTemplates(await getVoiceTemplates());
    } catch (err) {
      setError('Failed to load templates: ' + parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  // Filter templates by active category
  const visibleTemplates = (() => {
    const cat = CATEGORIES.find((c) => c.id === activeCategory);
    if (!cat || !cat.keys) return templates;
    return templates.filter((t) => cat.keys.includes(t.templateKey));
  })();

  const handleCreate = () => {
    const cat = CATEGORIES.find((c) => c.id === activeCategory);
    const defaultKey = cat?.keys?.[0] ?? 'ivrResupplyVoicemail';
    setEditingTemplate(null);
    setFormData({ ...DEFAULT_FORM, templateKey: defaultKey });
    setShowForm(true);
    setError('');
  };

  const handleEdit = (t) => {
    setEditingTemplate(t);
    setFormData({
      templateKey: t.templateKey,
      companyId: t.companyId ?? '',
      patientTypeId: t.patientTypeId ?? '',
      messageEnglish: t.messageEnglish,
      messageSpanish: t.messageSpanish ?? '',
      isActive: t.isActive,
      displayOrder: t.displayOrder,
    });
    setShowForm(true);
    setError('');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setError('');
  };

  const handleSave = async () => {
    if (!formData.messageEnglish.trim()) {
      setError('English message is required.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        templateKey: formData.templateKey,
        companyId: formData.companyId ? parseInt(formData.companyId) : null,
        patientTypeId: formData.patientTypeId ? parseInt(formData.patientTypeId) : null,
        messageEnglish: formData.messageEnglish.trim(),
        messageSpanish: formData.messageSpanish.trim() || null,
        isActive: formData.isActive,
        displayOrder: parseInt(formData.displayOrder) || 99,
      };

      if (editingTemplate) {
        await updateVoiceTemplate(editingTemplate.id, {
          messageEnglish: payload.messageEnglish,
          messageSpanish: payload.messageSpanish,
          isActive: payload.isActive,
          displayOrder: payload.displayOrder,
        });
        setSuccessMessage('Template updated.');
      } else {
        await createVoiceTemplate(payload);
        setSuccessMessage('Template created.');
      }
      setShowForm(false);
      setEditingTemplate(null);
      await fetchTemplates();
    } catch (err) {
      setError('Failed to save: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    const label = `"${t.templateKey}"${t.companyName ? ` for ${t.companyName}` : ' (global)'}`;
    if (!window.confirm(`Deactivate template ${label}?`)) return;
    try {
      setError('');
      await deleteVoiceTemplate(t.id);
      setSuccessMessage('Template deactivated.');
      await fetchTemplates();
    } catch (err) {
      setError('Failed to deactivate: ' + parseApiError(err));
    }
  };

  const field = (key, value) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  const activeCategoryMeta = CATEGORIES.find((c) => c.id === activeCategory);

  if (loading) return (
    <div className="voicemail-templates-tab">
      <h2>Voice Templates</h2>
      <p>Loading...</p>
    </div>
  );

  return (
    <div className="voicemail-templates-tab">
      {/* Header */}
      <div className="tab-header">
        <div>
          <h2>Voice Templates</h2>
          {activeCategoryMeta?.description && (
            <p className="vt-category-desc">{activeCategoryMeta.description}</p>
          )}
        </div>
        <button onClick={handleCreate} className="btn-add">+ New Template</button>
      </div>

      {/* Category filter pills */}
      <div className="vt-category-bar">
        {CATEGORIES.map((cat) => {
          const count = cat.keys
            ? templates.filter((t) => cat.keys.includes(t.templateKey)).length
            : templates.length;
          return (
            <button
              key={cat.id}
              className={`vt-category-pill ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => { setActiveCategory(cat.id); setShowForm(false); }}
            >
              {cat.label}
              <span className="vt-pill-count">{count}</span>
            </button>
          );
        })}
      </div>

      {error && <div className="vt-error">{error}</div>}
      {successMessage && <div className="vt-success">{successMessage}</div>}

      {/* Create / Edit Form */}
      {showForm && (
        <div className="vt-form-card">
          <h3>{editingTemplate ? 'Edit Template' : 'New Template'}</h3>
          <div className="vt-form-grid">
            <div className="vt-field">
              <label>Template Key</label>
              <select
                value={formData.templateKey}
                onChange={(e) => field('templateKey', e.target.value)}
                disabled={!!editingTemplate}
              >
                {ALL_TEMPLATE_KEYS.map((key) => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>

            <div className="vt-field">
              <label>Company ID <span className="field-hint">(blank = global)</span></label>
              <input
                type="number"
                value={formData.companyId}
                onChange={(e) => field('companyId', e.target.value)}
                placeholder="Leave blank for global default"
                disabled={!!editingTemplate}
              />
            </div>

            <div className="vt-field">
              <label>Patient Type</label>
              <select
                value={formData.patientTypeId}
                onChange={(e) => field('patientTypeId', e.target.value)}
                disabled={!!editingTemplate}
              >
                {PATIENT_TYPES.map((pt) => (
                  <option key={pt.value} value={pt.value}>{pt.label}</option>
                ))}
              </select>
            </div>

            <div className="vt-field">
              <label>Display Order</label>
              <input
                type="number"
                value={formData.displayOrder}
                onChange={(e) => field('displayOrder', e.target.value)}
              />
            </div>

            <div className="vt-field vt-field-wide">
              <label>English Message</label>
              <textarea
                value={formData.messageEnglish}
                onChange={(e) => field('messageEnglish', e.target.value)}
                rows="4"
                placeholder="Enter English message"
              />
            </div>

            <div className="vt-field vt-field-wide">
              <label>Spanish Message <span className="field-hint">(optional)</span></label>
              <textarea
                value={formData.messageSpanish}
                onChange={(e) => field('messageSpanish', e.target.value)}
                rows="4"
                placeholder="Enter Spanish message (optional)"
              />
            </div>

            <div className="vt-field vt-field-wide">
              <div className="placeholder-help">
                <strong>Available placeholders:</strong>
                <code>{'{CompanyName}'}</code> Company name
                <code>{'{CompanyPhoneNumber}'}</code> Callback number
                <code>{'{CallbackCode}'}</code> Patient reference code (IVR voicemail)
              </div>
            </div>

            <div className="vt-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => field('isActive', e.target.checked)}
                />
                Active
              </label>
            </div>
          </div>

          <div className="vt-form-actions">
            <button onClick={handleSave} disabled={saving} className="btn-save">
              {saving ? 'Saving...' : editingTemplate ? 'Update' : 'Create'}
            </button>
            <button onClick={handleCancel} className="btn-cancel">Cancel</button>
          </div>
        </div>
      )}

      {/* Templates Table */}
      <div className="vt-table-wrapper">
        <table className="vt-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Company</th>
              <th>Patient Type</th>
              <th>English Message</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visibleTemplates.length === 0 ? (
              <tr>
                <td colSpan="6" className="vt-empty">
                  No templates found for this category. Click &quot;+ New Template&quot; to create one.
                </td>
              </tr>
            ) : (
              visibleTemplates.map((t) => (
                <tr key={t.id} className={!t.isActive ? 'vt-inactive' : ''}>
                  <td className="vt-key">{t.templateKey}</td>
                  <td>{t.companyName || (t.companyId ? `ID: ${t.companyId}` : 'Global')}</td>
                  <td>{t.patientTypeDescription || 'All'}</td>
                  <td className="vt-message">{t.messageEnglish}</td>
                  <td>
                    <span className={`vt-status ${t.isActive ? 'active' : 'inactive'}`}>
                      {t.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="vt-actions">
                    <button onClick={() => handleEdit(t)} className="btn-edit">Edit</button>
                    {t.isActive && (
                      <button onClick={() => handleDelete(t)} className="btn-deactivate">Deactivate</button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
